import React from "react";
import { preOwned } from "../mock/mock";
import Seo from "../seo/Seo";
import { PAGE_SEO } from "../seo/siteConfig";
import { breadcrumbSchema, webPageSchema } from "../seo/schema";
import Reveal from "../components/common/Reveal";
import CircleArrowLink from "../components/common/CircleArrowLink";
import LocationCTA from "../components/sections/LocationCTA";
import { ArrowUpRight } from "lucide-react";

const PreOwnedDevices = () => {
  return (
    <>
      <Seo
        title={PAGE_SEO["/pre-owned-devices"].title}
        description={PAGE_SEO["/pre-owned-devices"].description}
        path="/pre-owned-devices"
        image={preOwned.aboutImage}
        schema={[
          webPageSchema({ ...PAGE_SEO["/pre-owned-devices"], path: "/pre-owned-devices" }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Pre-Owned Devices", path: "/pre-owned-devices" },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="relative min-h-[64vh] flex items-center overflow-hidden">
        <img
          src={preOwned.heroBg}
          alt="Certified pre-owned devices"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
        <div className="tnf-container relative pt-32 pb-20">
          <Reveal className="max-w-3xl">
            <span className="tnf-eyebrow text-[var(--tnf-orange)]">
              Certified Pre-Owned
            </span>
            <h1 className="font-display text-white font-extrabold leading-[1.05] text-[38px] sm:text-[54px] mt-3">
              {preOwned.heroTitle}
            </h1>
            <div className="mt-8">
              <CircleArrowLink to="/contact" variant="white">
                Check availability
              </CircleArrowLink>
            </div>
          </Reveal>
        </div>
      </section>

      {/* About */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="tnf-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal className="rounded-[28px] overflow-hidden shadow-[0_24px_70px_rgba(0,0,0,0.12)]">
              <img
                src={preOwned.aboutImage}
                alt="Pre-owned inventory"
                className="w-full h-full object-cover aspect-square"
              />
            </Reveal>
            <Reveal delay={120}>
              <span className="tnf-eyebrow">The Smart Upgrade</span>
              <h2 className="font-display text-[30px] sm:text-[42px] font-extrabold text-[#0e0e10] mt-3 leading-[1.1]">
                {preOwned.aboutTitle}
              </h2>
              <p className="text-[#3b3b3b]/80 text-[16px] mt-4 leading-relaxed">
                {preOwned.aboutText}
              </p>
              <div className="mt-7">
                <CircleArrowLink to="/repair-estimate" variant="orange">
                  Visit our kiosk
                </CircleArrowLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="py-20 lg:py-28">
        <div className="tnf-container">
          <Reveal className="max-w-2xl mb-12">
            <span className="tnf-eyebrow">Our Promise</span>
            <h2 className="font-display text-[30px] sm:text-[42px] font-extrabold text-[#0e0e10] mt-3 leading-[1.1]">
              {preOwned.guaranteeTitle}
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {preOwned.guarantee.map((g, i) => (
              <Reveal
                key={i}
                delay={i * 90}
                className="bg-white rounded-2xl border border-black/5 p-6 hover:shadow-[0_18px_50px_rgba(0,0,0,0.08)] hover:-translate-y-1.5 transition-all duration-300"
              >
                <div className="w-16 h-16 rounded-xl bg-[#f6f7f9] flex items-center justify-center mb-5 overflow-hidden">
                  <img src={g.icon} alt="" className="w-10 h-10 object-contain" />
                </div>
                <h3 className="font-display text-[17px] font-bold text-[#0e0e10] leading-snug">
                  {g.title}
                </h3>
                <p className="text-[14px] text-[#3b3b3b]/80 mt-2 leading-relaxed">
                  {g.text}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Inventory categories */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="tnf-container">
          <Reveal className="max-w-2xl mb-12">
            <span className="tnf-eyebrow">In Stock</span>
            <h2 className="font-display text-[30px] sm:text-[42px] font-extrabold text-[#0e0e10] mt-3 leading-[1.1]">
              {preOwned.inventoryTitle}
            </h2>
            <p className="text-[#3b3b3b]/80 text-[16px] mt-3">
              {preOwned.inventoryText}
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {preOwned.devices.map((d, i) => (
              <Reveal key={i} delay={i * 90}>
                <div className="group relative rounded-2xl overflow-hidden border border-black/5 bg-[#f6f7f9] hover:shadow-[0_22px_60px_rgba(0,0,0,0.12)] transition-all duration-300 hover:-translate-y-1.5">
                  <div className="relative aspect-square overflow-hidden bg-white">
                    <img
                      src={d.image}
                      alt={d.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between">
                      <h3 className="font-display text-[18px] font-bold text-[#0e0e10]">
                        {d.title}
                      </h3>
                      <span
                        className="w-7 h-7 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:rotate-45"
                        style={{ background: "var(--tnf-blue)" }}
                      >
                        <ArrowUpRight size={14} color="#fff" />
                      </span>
                    </div>
                    <p className="text-[13.5px] text-[#3b3b3b]/80 mt-2 leading-relaxed">
                      {d.text}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-12 text-center">
            <p className="text-[#3b3b3b]/80 text-[15px] mb-5">
              Looking for a specific model? Ask us — inventory changes weekly.
            </p>
            <CircleArrowLink to="/contact" variant="orange">
              Ask about a device
            </CircleArrowLink>
          </Reveal>
        </div>
      </section>

      <LocationCTA />
    </>
  );
};

export default PreOwnedDevices;
